import { types, flow, Instance, cast } from "mobx-state-tree";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";
import { logger } from "@fidget/common/utils";
import { servicesGetters, uiStoreGetter } from "../utils";

const TagGroupRule = types.model("Tag", {
  tagGroupId: types.string,
  targetTagGroupIds: types.array(types.string)
});

export const TagGroupRulesStore = types
  .model("TagGroupRulesStore", {
    rules: types.map(TagGroupRule),
    enabled: types.maybe(types.boolean)
  })
  .views(servicesGetters)
  .views(uiStoreGetter)
  .views((self) => {
    const views = {
      get allRules(): ITagGroupRule[] {
        return [...self.rules.values()];
      },

      getByTagGroup(tagGroupId: string): ITagGroupRule | undefined {
        return self.rules.get(tagGroupId);
      },

      get isEnabled(): boolean {
        return !!self.enabled;
      }
    };

    return views;
  })
  .actions((self) => {
    const actions = {
      setRules(rules: ITagGroupRule[]) {
        self.rules.clear();
        rules.forEach((item) => self.rules.set(item.tagGroupId, item));
      },

      addRule(rule: ITagGroupRule) {
        if (!self.rules.has(rule.tagGroupId)) {
          self.rules.set(rule.tagGroupId, rule);
        }
      },

      updateRule(tag: ITagGroupRule) {
        self.rules.set(tag.tagGroupId, tag);
      },

      deleteRule(tagGroupId: string) {
        self.rules.delete(tagGroupId);
      },

      setEnabled(isEnabled: boolean) {
        self.enabled = isEnabled;
      },

      loadRules: flow(function* () {
        try {
          const rules = yield self.spinnerService.getTagGroupRules();
          actions.setRules(rules);
        } catch (err) {
          logger.error({ message: "Couldn't load tag group transfer rules", error: err });
        }
      }),

      loadParameters: flow(function* () {
        try {
          const { enabled } = yield self.spinnerService.getTagGroupTransfersParameters();
          actions.setEnabled(enabled);
        } catch (err) {
          if ((err as Response).status !== HttpStatusCode.NOT_FOUND) {
            actions.setEnabled(false);
          }
          logger.error({
            message: "Couldn't load tag group transfer parameters rules",
            error: err
          });
        }
      }),

      loadRule: flow(function* (tagGroupId: string) {
        try {
          const loadedRule = yield self.spinnerService.getTagGroupRule(tagGroupId);
          actions.updateRule(loadedRule);
        } catch (err) {
          if ((err as Response).status !== HttpStatusCode.NOT_FOUND) {
            logger.error({ message: "Couldn't load tag group transfer rules", error: err });
            const defaultRule: ITagGroupRule = cast({ tagGroupId, targetTagGroupIds: [] });
            actions.updateRule(defaultRule);
          }
        }
      })
    };

    return actions;
  });

export interface ITagGroupRule extends Instance<typeof TagGroupRule> {}
